import React, { useEffect, useState } from "react";
import { Trash2, UserCheck, Save } from "lucide-react";
import { toast } from "../../components/ui/sonner";
import { api, errorMessage } from "../../api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

const SettingsCard = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/conversations/settings").then((r) => setS(r.data)).catch((e) => toast.error(errorMessage(e)));
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/conversations/settings", s);
      toast.success("Assistant settings saved");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  if (!s) return null;
  return (
    <div className="card p-5 md:p-6 space-y-4" data-testid="assistant-settings">
      <div className="flex items-center justify-between gap-4">
        <h3 className="font-display font-bold">Assistant settings</h3>
        <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
          <input type="checkbox" className="w-4 h-4 accent-[var(--fr-primary)]" checked={s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} data-testid="assistant-enabled" />
          {s.enabled ? "Live on site" : "Hidden"}
        </label>
      </div>
      <label className="block text-sm font-medium">
        Welcome message
        <input className="fr-input !h-10 text-sm mt-1.5" placeholder="Hi! I'm the Fleetroot assistant…" value={s.welcome} onChange={(e) => setS({ ...s, welcome: e.target.value })} data-testid="assistant-welcome" />
      </label>
      <label className="block text-sm font-medium">
        Extra instructions <span className="text-xs font-normal text-[var(--fr-muted)]">(e.g. current promotions, tone, things to avoid)</span>
        <textarea className="fr-input text-sm mt-1.5" rows={3} value={s.extraInstructions} onChange={(e) => setS({ ...s, extraInstructions: e.target.value })} data-testid="assistant-instructions" />
      </label>
      <div className="rounded-xl border border-[var(--fr-border)] p-4" data-testid="assistant-office-hours">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm font-medium">Human handoff hours <span className="text-xs font-normal text-[var(--fr-muted)]">— outside these, the assistant only collects the lead</span></div>
          {s.openNow !== undefined && (
            <span className={`chip !text-[10px] uppercase tracking-wide ${s.openNow ? "!bg-emerald-50 !text-emerald-700 !border-emerald-100" : "!bg-amber-50 !text-amber-700 !border-amber-100"}`} data-testid="assistant-open-now">
              {s.openNow ? "Team online now" : "After hours now"}
            </span>
          )}
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="assistant-days">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => {
            const on = (s.officeHours?.days || []).includes(i);
            return (
              <button key={d} type="button" onClick={() => setS({ ...s, officeHours: { ...s.officeHours, days: on ? s.officeHours.days.filter((x) => x !== i) : [...(s.officeHours?.days || []), i].sort() } })} className={`h-8 px-3 rounded-full text-xs font-medium border transition-colors ${on ? "bg-[var(--fr-ink)] text-white border-[var(--fr-ink)]" : "bg-white text-[var(--fr-ink-2)] border-[var(--fr-border)]"}`} data-testid={`assistant-day-${i}`}>
                {d}
              </button>
            );
          })}
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          <label className="block font-medium">From<input type="time" className="fr-input !h-10 text-sm mt-1" value={s.officeHours?.start || "09:00"} onChange={(e) => setS({ ...s, officeHours: { ...s.officeHours, start: e.target.value } })} data-testid="assistant-hours-start" /></label>
          <label className="block font-medium">To<input type="time" className="fr-input !h-10 text-sm mt-1" value={s.officeHours?.end || "17:00"} onChange={(e) => setS({ ...s, officeHours: { ...s.officeHours, end: e.target.value } })} data-testid="assistant-hours-end" /></label>
          <label className="block font-medium col-span-2 sm:col-span-1">Timezone<input className="fr-input !h-10 text-sm mt-1 font-mono" value={s.officeHours?.timezone || "Asia/Dubai"} onChange={(e) => setS({ ...s, officeHours: { ...s.officeHours, timezone: e.target.value } })} data-testid="assistant-hours-tz" /></label>
        </div>
      </div>
      <button type="button" onClick={save} disabled={busy} className="btn btn-primary btn-sm disabled:opacity-60" data-testid="assistant-save">
        <Save size={14} className="mr-1.5" /> {busy ? "Saving…" : "Save"}
      </button>
    </div>
  );
};

export default function AdminConversations() {
  const [list, setList] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/admin/conversations").then((r) => setList(r.data)).catch((e) => toast.error(errorMessage(e)));
  }, []);

  const remove = async (c) => {
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await api.delete(`/admin/conversations/${c.sessionId}`);
      setList((l) => l.filter((x) => x.sessionId !== c.sessionId));
      if (active?.sessionId === c.sessionId) setActive(null);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <div data-testid="admin-conversations-page" className="space-y-6">
      <div>
        <span className="eyebrow">AI assistant</span>
        <h1 className="font-display font-bold text-3xl md:text-4xl mt-2">Conversations</h1>
        <p className="text-sm text-[var(--fr-muted)] mt-2">Every chat with the website assistant. Conversations that produced a demo request are marked as leads.</p>
      </div>
      <SettingsCard />
      <div className="grid lg:grid-cols-[360px_1fr] gap-5 items-start">
        <div className="card overflow-hidden">
          {list === null ? (
            <div className="p-8 text-center text-sm text-[var(--fr-muted)]">Loading…</div>
          ) : list.length === 0 ? (
            <div className="p-8 text-center text-sm text-[var(--fr-muted)]" data-testid="conversations-empty">No conversations yet.</div>
          ) : (
            <ul className="divide-y divide-[var(--fr-border)] max-h-[70vh] overflow-y-auto" data-testid="conversations-list">
              {list.map((c) => (
                <li key={c.sessionId}>
                  <button type="button" onClick={() => setActive(c)} className={`w-full text-left p-4 hover:bg-[var(--fr-bg)] ${active?.sessionId === c.sessionId ? "bg-[var(--fr-primary-tint)]/50" : ""}`} data-testid="conversation-item">
                    <div className="flex items-center gap-2 text-xs text-[var(--fr-muted)]">
                      <span>{fmt(c.updatedAt)}</span>
                      <span>· {c.messageCount} msgs</span>
                      {c.afterHoursAt && !c.handoffAt && <span className={`${c.leadId ? "" : "ml-auto"} chip !text-[10px] uppercase !bg-[var(--fr-bg-alt)] !text-[var(--fr-muted)]`} data-testid="conversation-afterhours-badge">Asked for human · after hours</span>}
                      {c.handoffAt && <span className={`${c.leadId ? "" : "ml-auto"} chip !text-[10px] uppercase !bg-amber-50 !text-amber-700 !border-amber-100`} data-testid="conversation-handoff-badge">Asked for human</span>}
                      {c.leadId && <span className={`${c.handoffAt ? "" : "ml-auto"} chip !text-[10px] uppercase !bg-emerald-50 !text-emerald-700 !border-emerald-100 inline-flex items-center gap-1`} data-testid="conversation-lead-badge"><UserCheck size={10} /> Lead</span>}
                    </div>
                    <div className="text-sm font-medium mt-1 truncate">{c.preview || "(no message)"}</div>
                    <div className="text-xs text-[var(--fr-muted)] mt-0.5 truncate">{c.leadEmail || c.page || ""}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="card p-5 min-h-[300px]" data-testid="conversation-detail">
          {!active ? (
            <p className="text-sm text-[var(--fr-muted)]">Select a conversation to read the transcript.</p>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 pb-4 border-b border-[var(--fr-border)]">
                <div className="text-xs text-[var(--fr-muted)]">Started {fmt(active.createdAt)} · page {active.page || "/"} {active.leadEmail && <>· lead <span className="font-semibold text-[var(--fr-ink)]">{active.leadEmail}</span></>}</div>
                <button type="button" onClick={() => remove(active)} className="icon-btn danger" title="Delete" data-testid="conversation-delete"><Trash2 size={14} /></button>
              </div>
              <div className="mt-4 space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                {active.messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[var(--fr-primary)] text-white" : "bg-[var(--fr-bg)] border border-[var(--fr-border)]"}`}>{m.content}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
